// splash.js — tap-to-start overlay used to capture the first user
// gesture so audio can be unlocked.
//
// Why this exists:
//   Modern browsers (and iOS Safari in particular) refuse to play audio
//   until the user has interacted with the page. We need *some* gesture
//   to arrive before sound-manager's unlock() will work, and we want
//   that gesture to be an unmistakable "child presses the screen" —
//   not an accidental scroll or a hidden autoplay poke.
//
//   A full-screen overlay with a friendly "Tap to start" label is the
//   clearest way to do that: the child (or parent) can't miss it, and
//   the first tap *anywhere* serves as the unlock gesture.
//
// Dismissal:
//   The overlay listens for `click` (plus Enter/Space for keyboard/
//   switch accessibility) and dismisses on the first event. We
//   deliberately do NOT dismiss on pointerdown: on iOS Safari, a
//   pointerdown-driven dismissal that runs synchronously will remove
//   the overlay (or apply `pointer-events: none`) before the synthetic
//   `click` event fires later in the same gesture. Safari then does
//   fresh hit-testing at click time, finds a grid button underneath,
//   and delivers the click there — causing a stray button press the
//   instant the splash dismisses. Listening for click directly avoids
//   the race entirely: the click target is locked to the splash at
//   pointerdown time, so the event can never fall through.
//   After dismissal, the overlay is removed from the DOM and
//   listeners are cleaned up.
//
// onDismiss:
//   The caller supplies an async `onDismiss` callback. We await its
//   return value before fully removing the overlay, which gives the
//   app a chance to unlock() the sound backend on the gesture stack
//   — Safari ties the "user-gesture token" to the event handler, so
//   the unlock has to start inside this click path. We don't block
//   forever though: if onDismiss rejects, we still tear down.

import { t } from './i18n.js';

/**
 * @typedef {Object} Splash
 * @property {HTMLElement} element  The overlay root element.
 * @property {() => void} dismiss   Force-dismiss the splash (e.g. from tests).
 * @property {boolean} dismissed    True once the splash has been dismissed.
 */

/**
 * Detect whether we're running on an iPhone or iPad.
 *
 * iPadOS 13+ reports its user agent as a Mac, so we additionally check
 * for multi-touch support with a MacIntel platform — that combination
 * only happens on iPads. The classic iPhone/iPod string handles the
 * straightforward cases.
 *
 * Exported so tests can monkey-patch or stub if needed, and so the
 * splash has an opt-in override via `opts.isIOS`.
 */
export function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS 13+ desktop-mode trick.
  if (
    navigator.platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

/**
 * Detect desktop Safari (macOS, not iOS/iPadOS).
 *
 * Safari is the only major browser whose UA contains "Safari/" but NOT
 * "Chrome/" or "Chromium/" (which Chrome, Edge, Opera, etc. all include).
 * We exclude iOS/iPadOS because those devices get the ring/silent switch
 * hint instead — the auto-play setting issue is specific to desktop Safari.
 *
 * Exported so tests can stub it and so the splash has an opt-in override
 * via `opts.isDesktopSafari`.
 */
export function detectDesktopSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isSafari = /Safari\//.test(ua) && !/Chrome\/|Chromium\//.test(ua);
  return isSafari && !detectIOS();
}

/**
 * Create a tap-to-start splash overlay and attach it to `parent`.
 *
 * @param {HTMLElement} parent
 * @param {{
 *   onDismiss?: () => void | Promise<void>,
 *   isIOS?: boolean,
 *   isDesktopSafari?: boolean,
 * }} [opts]
 * @returns {Splash}
 */
export function createSplash(parent, opts = {}) {
  if (!(parent instanceof HTMLElement)) {
    throw new TypeError('createSplash: parent must be an element');
  }

  const onDismiss = opts.onDismiss ?? (() => {});
  // Platform detection is injectable so tests can assert each branch
  // without monkey-patching `navigator.userAgent`.
  const isIOS = opts.isIOS ?? detectIOS();
  const isDesktopSafari = opts.isDesktopSafari ?? detectDesktopSafari();

  const overlay = document.createElement('div');
  overlay.className = 'splash';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', t('app.title'));

  // Inner "card" wrapper — the whole overlay is the hit area, but the
  // visual centerpiece is a button the child can clearly aim at.
  const inner = document.createElement('div');
  inner.className = 'splash__inner';

  // App icon (sheep) — this is the same mark used as the PWA/iOS home
  // screen icon and carries brand continuity from the original native
  // iOS version of this app. 512 is the largest size we ship, so it
  // scales down cleanly on any display density.
  const icon = document.createElement('img');
  icon.className = 'splash__icon';
  icon.src = '/icons/icon-512.png';
  icon.alt = '';
  icon.decoding = 'async';
  icon.fetchPriority = 'high';
  inner.appendChild(icon);

  const heading = document.createElement('h2');
  heading.className = 'splash__title';
  heading.textContent = t('app.title');
  inner.appendChild(heading);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'splash__button';
  button.textContent = t('app.tapToStart');
  // The button is mostly decorative — the overlay itself catches taps
  // — but making it focusable gives keyboard users something to aim
  // at and is a free accessibility win.
  inner.appendChild(button);

  // Platform-specific audio hints. Each platform has its own common
  // "no sound" footgun that we can't detect or bypass reliably, so
  // the honest fix is to tell the user up front.
  //
  //   iOS: the physical ring/silent switch silences Web Audio.
  //   Desktop Safari: the per-site "Auto-Play" setting (default:
  //     "Stop Media with Sound") blocks AudioContext output even
  //     when ctx.state reports "running".
  //
  // Only one hint is shown — iOS takes priority because the ring
  // switch is the more common gotcha on that platform.
  if (isIOS) {
    const hint = document.createElement('p');
    hint.className = 'splash__hint';
    hint.textContent = t('app.iosRingSwitchHint');
    inner.appendChild(hint);
  } else if (isDesktopSafari) {
    const hint = document.createElement('p');
    hint.className = 'splash__hint';
    hint.textContent = t('app.safariAutoPlayHint');
    inner.appendChild(hint);
  }

  overlay.appendChild(inner);
  parent.appendChild(overlay);

  let dismissed = false;

  /**
   * Dismiss the splash. Safe to call multiple times; only the first
   * call does any work.
   */
  function dismiss() {
    if (dismissed) return;
    dismissed = true;

    removeListeners();

    // Fire onDismiss synchronously from within the gesture handler so
    // audio unlock happens on the user-gesture stack. We don't await
    // it here (doing so would defer unlock past the gesture). If the
    // callback returns a promise, we attach a handler so unhandled
    // rejections don't bubble, but we don't block teardown on it.
    let result;
    try {
      result = onDismiss();
    } catch (err) {
      console.warn('[splash] onDismiss threw:', err);
    }
    if (result && typeof result.catch === 'function') {
      result.catch((err) => {
        console.warn('[splash] onDismiss rejected:', err);
      });
    }

    // Fade out via a class toggle, then remove. CSS owns the actual
    // timing; we use a short transitionend listener with a timeout
    // safety net in case no transition is defined (tests / jsdom).
    overlay.classList.add('splash--dismissed');
    const remove = () => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    };
    let removed = false;
    const onEnd = () => {
      if (removed) return;
      removed = true;
      overlay.removeEventListener('transitionend', onEnd);
      remove();
    };
    overlay.addEventListener('transitionend', onEnd);
    // Safety net — if no transition runs, still tear down.
    setTimeout(onEnd, 400);
  }

  // Any click anywhere on the overlay dismisses it. We listen for
  // `click` (not `pointerdown`) specifically to avoid the iOS Safari
  // click fall-through described in the module header: the click
  // target is locked at pointerdown time, so by the time click fires
  // the splash is still the target even if a later step of dismissal
  // has begun. Enter/Space on the focused button dismiss via keydown
  // for keyboard and switch-control accessibility.
  const handleClick = (e) => {
    // Ignore non-primary button clicks (middle/right mouse).
    if (e.button !== undefined && e.button !== 0) return;
    dismiss();
  };
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      dismiss();
    }
  };

  overlay.addEventListener('click', handleClick);
  overlay.addEventListener('keydown', handleKey);

  function removeListeners() {
    overlay.removeEventListener('click', handleClick);
    overlay.removeEventListener('keydown', handleKey);
  }

  // Move focus to the button so Enter/Space work immediately for
  // keyboard users. Guarded because focus() can throw in some
  // environments (detached nodes, jsdom quirks).
  try {
    button.focus({ preventScroll: true });
  } catch {
    /* non-fatal */
  }

  return {
    element: overlay,
    dismiss,
    get dismissed() {
      return dismissed;
    },
  };
}
